import { ErpTable, EmptyRow, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate } from '@/shared/lib/format'
import { getPollOfPolls, listExternalPolls } from './api'
import { Pill, count, pct } from './vocab'

/**
 * What everybody else published, and an average that admits how it was made.
 *
 * ## Bringing this in is one of the module's clearest wins
 *
 * PollSphere has an `ExternalPoll` model attached to candidates and consumed by
 * the candidate drawer — never by its Polling Studio. An in-house poll is far
 * more useful next to what the other houses published, and a poll-of-polls is the
 * first thing anybody asks for.
 *
 * ## The weighting rule is on the chart
 *
 * Every trend line in polling coverage is somebody's undisclosed weighting of
 * somebody else's polls. This one states its rule, in words, in the response —
 * so a reader can disagree with it rather than having to guess at it.
 */
export function ExternalPollsPage() {
  const canRead = useCan('survey:read')
  const canAnalyse = useCan('survey:analyse')

  const polls = useLoad(() => listExternalPolls(), [])
  const average = useLoad(
    () => (canAnalyse ? getPollOfPolls() : Promise.resolve(null)), [canAnalyse])

  if (!canRead) return <Forbidden what="external polls" />

  const rows = polls.data ?? []
  const pop = average.data

  return (
    <div className="space-y-4">
      <SectionHeader
        title="External polls"
        description="Published figures from other houses, and the average they make together."
      />

      <ErrorStrip message={polls.error ?? average.error} />

      {polls.loading && rows.length === 0 ? <LoadingBlock label="Loading polls…" /> : (
        <>
          {pop && pop.averages.length > 0 && (
            <Panel
              title="Poll of polls"
              description={pop.unitName ? `Within ${pop.unitName}` : 'Everything published'}
            >
              <div className="space-y-2">
                {pop.averages.map((row) => (
                  <div key={row.subject}
                    className="grid grid-cols-[minmax(140px,26%)_1fr_auto] items-center gap-3">
                    <span className="truncate text-erp-sm">{row.subject}</span>
                    <span className="h-2 rounded bg-muted overflow-hidden">
                      <span className="block h-full bg-chocolate"
                        style={{ width: `${Math.max(0, Math.min(100, Number(row.share) * 100))}%` }} />
                    </span>
                    <span className="tabular-nums text-erp-sm whitespace-nowrap">
                      {pct(row.share)}
                      <span className="ml-1.5 text-erp-xs text-muted-foreground">
                        {row.contributingPolls} poll{row.contributingPolls === 1 ? '' : 's'}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                {/*
                  The rule, in words, where the number is. Not in a tooltip and not
                  in a help page: an average whose weighting is somewhere else is
                  an average nobody can argue with.
                */}
                <Note tone="info">{pop.weightingRule}</Note>
              </div>
              {pop.averages.some((a) => a.contributingPolls === 1) && (
                <div className="mt-2">
                  <Note tone="bad">
                    At least one line is an average of a single poll. That is that poll, and
                    reading it as a consensus would be reading it wrong.
                  </Note>
                </div>
              )}
            </Panel>
          )}

          <Panel title="Published polls" description="Newest first. Every row carries its source.">
            <ErpTable head={(
              <>
                <Th>Pollster</Th>
                <Th>Date</Th>
                <Th>Seat</Th>
                <Th>Where</Th>
                <Th className="text-right">n</Th>
                <Th className="text-right">MoE</Th>
                <Th>Leading</Th>
                <Th>Source</Th>
              </>
            )}>
              {rows.length === 0 ? (
                <EmptyRow colSpan={8} message={
                  'Nothing recorded yet. An in-house poll is far more useful next to what the '
                  + 'other houses published.'
                } />
              ) : rows.map((poll) => {
                const leading = poll.results
                  .filter((r) => !r.undecided)
                  .slice()
                  .sort((a, b) => Number(b.share) - Number(a.share))[0]
                return (
                  <tr key={poll.id} className="hover:bg-muted/40">
                    <Td>{poll.pollsterName}</Td>
                    <Td className="text-muted-foreground">{fmtDate(poll.pollDate)}</Td>
                    <Td className="text-muted-foreground">{poll.seatType ?? '—'}</Td>
                    <Td className="text-muted-foreground">{poll.unitName ?? 'National'}</Td>
                    <Td className="text-right tabular-nums">
                      {poll.sampleSize === null ? '—' : count(poll.sampleSize)}
                    </Td>
                    <Td className="text-right tabular-nums text-muted-foreground">
                      {poll.moe === null ? '—' : `±${Number(poll.moe).toFixed(1)}pp`}
                    </Td>
                    <Td>
                      {leading ? (
                        <span>
                          {leading.candidateName ?? leading.partyName ?? leading.label}
                          <span className="ml-1.5 tabular-nums text-muted-foreground">
                            {pct(leading.share)}
                          </span>
                        </span>
                      ) : '—'}
                    </Td>
                    <Td>
                      {poll.sourceUrl ? (
                        <a className="text-chocolate hover:underline text-erp-xs"
                          href={poll.sourceUrl} target="_blank" rel="noopener noreferrer">
                          link
                        </a>
                      ) : <span className="text-erp-xs text-muted-foreground">—</span>}
                    </Td>
                  </tr>
                )
              })}
            </ErpTable>
          </Panel>

          {pop && (
            <Panel title="Every published figure" description="Our own estimates are not merged in here — see the note.">
              <ErpTable head={(
                <>
                  <Th>Date</Th>
                  <Th>Source</Th>
                  <Th>Subject</Th>
                  <Th className="text-right">Share</Th>
                  <Th className="text-right">MoE</Th>
                  <Th className="text-right">n</Th>
                </>
              )}>
                {pop.points.length === 0 ? (
                  <EmptyRow colSpan={6} message="Nothing in the window." />
                ) : pop.points.map((point, i) => (
                  <tr key={`${point.source}-${point.subject}-${i}`} className="hover:bg-muted/40">
                    <Td className="text-muted-foreground">{fmtDate(point.date)}</Td>
                    <Td>
                      {point.ours ? <Pill>ours</Pill> : point.source}
                    </Td>
                    <Td>{point.subject}</Td>
                    <Td className="text-right tabular-nums">{pct(point.share)}</Td>
                    <Td className="text-right tabular-nums text-muted-foreground">
                      {point.moe === null ? '—' : `±${Number(point.moe).toFixed(1)}pp`}
                    </Td>
                    <Td className="text-right tabular-nums text-muted-foreground">
                      {point.sampleSize === null ? '—' : count(point.sampleSize)}
                    </Td>
                  </tr>
                ))}
              </ErpTable>
              <div className="mt-3">
                <Note tone="info">
                  Our own estimates sit on the Results screen with their own base and margin.
                  Averaging them into a figure we then compare ourselves against would be
                  circular, and a reader could not tell how much of the line was us.
                </Note>
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  )
}
